import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useCampaignAliases,
  useCampaigns,
  useRenameCampaign,
  useRevertCampaignName,
} from '@/api/campaigns'
import {
  getCampaignNamingPreview,
  useCampaignNamingPreview,
  usePpcCodes,
  type CampaignNamingPreview,
} from '@/api/naming'
import { useProfiles } from '@/api/profiles'
import { ApiError } from '@/api/client'
import { campaignNameErrorMessage as errorMessage } from '@/api/campaign-name-errors'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import Card from '@/components/Card'

const EXCLUSIVE_FAMILY_CONFIRMATION =
  'Diese Kampagne gehört ausschließlich zu dieser Produktfamilie und ist keine allgemeine Markenkampagne.'

function selectionKey(profileId: number, campaignId: number): string {
  return `${profileId}:${campaignId}`
}

function previewSignature(preview: CampaignNamingPreview): string {
  return JSON.stringify({
    campaignId: preview.campaignId,
    profileId: preview.profileId,
    amazonCampaignId: preview.amazonCampaignId,
    currentName: preview.currentName,
    proposedName: preview.proposedName,
    parentAsin: preview.parentAsin,
    ppcCode: preview.ppcCode,
    targeting: preview.targeting,
    childAsins: preview.childAsins,
    inferredChildAsins: preview.inferredChildAsins,
    blockers: preview.blockers,
    warnings: preview.warnings,
    mappingImportedAt: preview.mappingImportedAt,
  })
}

export default function ExistingCampaignRename() {
  const queryClient = useQueryClient()
  const [profileId, setProfileId] = useState<number | null>(null)
  const [campaignId, setCampaignId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [familyConfirmed, setFamilyConfirmed] = useState(false)
  const [inferenceConfirmed, setInferenceConfirmed] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [messageKind, setMessageKind] = useState<'success' | 'error' | 'reconcile' | null>(null)
  const [reconciliationLatches, setReconciliationLatches] = useState<Record<string, true>>({})
  const renameLock = useRef(false)
  const revertLock = useRef(false)
  const selectedRef = useRef({ profileId, campaignId })

  const profilesQuery = useProfiles()
  const campaignsQuery = useCampaigns(
    { profileId: profileId ?? undefined, search: search || undefined, limit: 25, offset, sortBy: 'name', sortDir: 'asc' },
    { enabled: profileId !== null },
  )
  const previewQuery = useCampaignNamingPreview(campaignId, profileId)
  const aliasesQuery = useCampaignAliases(campaignId)
  const ppcCodesQuery = usePpcCodes()
  const renameMutation = useRenameCampaign()
  const revertMutation = useRevertCampaignName()

  const preview = previewQuery.data
    && previewQuery.data.campaignId === campaignId
    && previewQuery.data.profileId === profileId
    ? previewQuery.data
    : null
  const signature = preview ? previewSignature(preview) : ''

  useEffect(() => {
    setFamilyConfirmed(false)
    setInferenceConfirmed(false)
  }, [profileId, campaignId, signature])

  useEffect(() => {
    setMessage(null)
    setMessageKind(null)
  }, [profileId, campaignId])

  useEffect(() => {
    selectedRef.current = { profileId, campaignId }
  }, [profileId, campaignId])

  const ppcCode = useMemo(
    () => preview?.ppcCode ? ppcCodesQuery.data?.codes.find((code) => code.code === preview.ppcCode) : undefined,
    [ppcCodesQuery.data?.codes, preview?.ppcCode],
  )
  const directoryReady = preview?.ppcCode === null || ppcCode !== undefined
  const needsInferenceConfirmation = preview?.warnings.includes('INFERRED_MAPPING_REQUIRES_REVIEW') ?? false
  const isConform = preview !== null
    && preview.blockers.length === 0
    && preview.proposedName !== null
    && preview.currentName === preview.proposedName
  const reconciliationRequired = profileId !== null
    && campaignId !== null
    && reconciliationLatches[selectionKey(profileId, campaignId)] === true
  const canRename = preview !== null
    && preview.proposedName !== null
    && !isConform
    && preview.blockers.length === 0
    && directoryReady
    && familyConfirmed
    && (!needsInferenceConfirmation || inferenceConfirmed)
    && !IS_DEMO_MODE
    && !renameMutation.isPending
    && !reconciliationRequired

  const aliases = aliasesQuery.data?.aliases ?? []
  const newestAlias = aliases[0]
  const canRevert = !IS_DEMO_MODE
    && preview !== null
    && newestAlias !== undefined
    && newestAlias.newName === preview.currentName
    && newestAlias.oldName !== preview.currentName
    && !reconciliationRequired

  function resetForSelection(nextProfileId: number | null, nextCampaignId: number | null): void {
    selectedRef.current = { profileId: nextProfileId, campaignId: nextCampaignId }
    setProfileId(nextProfileId)
    setCampaignId(nextCampaignId)
    setFamilyConfirmed(false)
    setInferenceConfirmed(false)
    setMessage(null)
    setMessageKind(null)
  }

  async function refreshSelected(): Promise<void> {
    await Promise.all([
      previewQuery.refetch({ throwOnError: true }),
      campaignsQuery.refetch({ throwOnError: true }),
      aliasesQuery.refetch({ throwOnError: true }),
    ])
  }

  function requireReconciliation(target: CampaignNamingPreview, operation: 'rename' | 'revert'): void {
    setReconciliationLatches((current) => ({ ...current, [selectionKey(target.profileId, target.campaignId)]: true }))
    if (selectedRef.current.profileId === target.profileId && selectedRef.current.campaignId === target.campaignId) {
      setFamilyConfirmed(false)
      setInferenceConfirmed(false)
      setMessage(errorMessage(new Error('unconfirmed'), operation))
      setMessageKind('reconcile')
    }
  }

  async function handleRename(): Promise<void> {
    if (!preview || !canRename || renameLock.current) return
    renameLock.current = true
    setMessage(null)
    setMessageKind(null)
    let freshPreview: CampaignNamingPreview
    try {
      freshPreview = await getCampaignNamingPreview(preview.campaignId, preview.profileId)
    } catch {
      if (selectedRef.current.profileId === preview.profileId && selectedRef.current.campaignId === preview.campaignId) {
        setFamilyConfirmed(false)
        setInferenceConfirmed(false)
        setMessage('Preview konnte vor dem Umbenennen nicht aktualisiert werden. Bitte erneut prüfen und bestätigen.')
        setMessageKind('error')
      }
      renameLock.current = false
      return
    }
    try {
      if (selectedRef.current.profileId !== preview.profileId || selectedRef.current.campaignId !== preview.campaignId) return
      if (previewSignature(freshPreview) !== previewSignature(preview)) {
        queryClient.setQueryData(
          ['naming', 'campaign-preview', preview.profileId, preview.campaignId],
          freshPreview,
        )
        setFamilyConfirmed(false)
        setInferenceConfirmed(false)
        setMessage('Der Preview hat sich vor dem Schreiben geändert. Bitte erneut prüfen und bestätigen.')
        setMessageKind('error')
        return
      }
      const response = await renameMutation.mutateAsync({
        id: preview.campaignId,
        name: preview.proposedName!,
        expectedName: preview.currentName,
      })
      try {
        await refreshSelected()
      } catch {
        requireReconciliation(preview, 'rename')
        return
      }
      if (selectedRef.current.profileId !== preview.profileId || selectedRef.current.campaignId !== preview.campaignId) return
      setFamilyConfirmed(false)
      setInferenceConfirmed(false)
      setMessage(`Kampagne erfolgreich umbenannt in „${response.campaign.name}“.`)
      setMessageKind('success')
    } catch (error) {
      const definitiveError = error instanceof ApiError && (error.status === 403 || error.status === 423 || error.status === 409 || error.code === 'SYNC_FRESHNESS_UNKNOWN')
      if (!definitiveError) {
        try { await refreshSelected() } catch { /* Der Abgleichstatus bleibt auch bei fehlendem Readback erhalten. */ }
        requireReconciliation(preview, 'rename')
        return
      }
      try { await refreshSelected() } catch { /* Die bekannte Fehlermeldung bleibt maßgeblich. */ }
      if (selectedRef.current.profileId !== preview.profileId || selectedRef.current.campaignId !== preview.campaignId) return
      setFamilyConfirmed(false)
      setInferenceConfirmed(false)
      setMessage(errorMessage(error, 'rename'))
      setMessageKind('error')
    } finally {
      renameLock.current = false
    }
  }

  async function handleRevert(): Promise<void> {
    if (!preview || !canRevert || revertLock.current) return
    revertLock.current = true
    setMessage(null)
    setMessageKind(null)
    try {
      const response = await revertMutation.mutateAsync({ id: preview.campaignId })
      try {
        await refreshSelected()
      } catch {
        requireReconciliation(preview, 'revert')
        return
      }
      if (selectedRef.current.profileId !== preview.profileId || selectedRef.current.campaignId !== preview.campaignId) return
      setMessage(`Kampagnenname erfolgreich zurückgesetzt auf „${response.campaign.name}“.`)
      setMessageKind('success')
    } catch (error) {
      const definitiveError = error instanceof ApiError && (error.status === 403 || error.status === 423 || error.status === 409 || error.code === 'SYNC_FRESHNESS_UNKNOWN')
      if (!definitiveError) {
        try { await refreshSelected() } catch { /* Der Abgleichstatus bleibt auch bei fehlendem Readback erhalten. */ }
        requireReconciliation(preview, 'revert')
        return
      }
      try { await refreshSelected() } catch { /* Die bekannte Fehlermeldung bleibt maßgeblich. */ }
      if (selectedRef.current.profileId !== preview.profileId || selectedRef.current.campaignId !== preview.campaignId) return
      setMessage(errorMessage(error, 'revert'))
      setMessageKind('error')
    } finally {
      revertLock.current = false
    }
  }

  return (
    <Card className="p-4">
      <h2 className="text-[15px] font-bold tracking-tight text-[var(--foreground)]">Vorhandene Kampagne umbenennen</h2>
      <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">Preview aus lokalen Daten prüfen und erst danach bestätigen.</p>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="text-xs font-medium text-[var(--foreground)]">
          Profil
          <select aria-label="Profil" value={profileId ?? ''} onChange={(event) => {
            setOffset(0)
            resetForSelection(event.target.value ? Number(event.target.value) : null, null)
          }} className="mt-1 block w-full rounded border border-[var(--border)] bg-[var(--card)] p-2">
            <option value="">Profil auswählen</option>
            {(profilesQuery.data ?? []).map((profile) => <option key={profile.id} value={profile.id}>{profile.name} ({profile.marketplace})</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-[var(--foreground)]">
          Kampagnen suchen
          <input aria-label="Kampagnen suchen" value={search} disabled={profileId === null} onChange={(event) => {
            setSearch(event.target.value)
            setOffset(0)
            resetForSelection(profileId, null)
          }} className="mt-1 block w-full rounded border border-[var(--border)] bg-[var(--card)] p-2" />
        </label>
      </div>

      {profileId !== null && (
        <div className="mt-3 space-y-2" aria-label="Kampagnenliste">
          {(campaignsQuery.data?.campaigns ?? []).map((campaign) => (
            <button key={campaign.id} type="button" onClick={() => resetForSelection(profileId, campaign.id)} className="block w-full rounded border border-[var(--border)] p-2 text-left text-xs hover:bg-[var(--muted)]">
              {campaign.name} <span className="font-mono text-[var(--muted-foreground)]">({campaign.amazonCampaignId})</span>
            </button>
          ))}
          {(campaignsQuery.data?.total ?? 0) > 25 && <div className="flex gap-2 text-xs">
            <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Vorherige Seite</button>
            <button type="button" disabled={offset + 25 >= (campaignsQuery.data?.total ?? 0)} onClick={() => setOffset(offset + 25)}>Nächste Seite</button>
          </div>}
        </div>
      )}

      {previewQuery.isError && <p role="alert" className="mt-3 text-xs text-red-700">Preview konnte nicht geladen werden. Es wird kein Ersatzname angezeigt.</p>}
      {preview && <div className="mt-4 space-y-3 rounded border border-[var(--border)] bg-[var(--muted)]/30 p-3 text-xs">
        <div><span className="text-[var(--muted-foreground)]">Aktueller Name: </span><span className="font-mono">{preview.currentName}</span></div>
        <div><span className="text-[var(--muted-foreground)]">Vorgeschlagener Name: </span><span className="font-mono">{preview.proposedName ?? '—'}</span></div>
        <div>Parent-ASIN: <span className="font-mono">{preview.parentAsin ?? '—'}</span></div>
        <div>Child-ASINs: <span className="font-mono">{preview.childAsins.join(', ') || '—'}</span></div>
        <div>PPC-Code: <span className="font-mono">{preview.ppcCode ?? '—'}</span>{ppcCode && <span> · {ppcCode.lager} · {ppcCode.category}</span>}</div>
        {preview.ppcCode !== null && ppcCodesQuery.isLoading && <p className="text-[var(--muted-foreground)]">PPC-Code-Verzeichnis wird geladen. Bestätigung bleibt gesperrt.</p>}
        {preview.ppcCode !== null && ppcCodesQuery.isError && <p role="alert" className="text-red-700">PPC-Code-Verzeichnis konnte nicht geladen werden. Bestätigung bleibt gesperrt.</p>}
        {!directoryReady && <p role="alert" className="text-red-700">PPC-Code-Verzeichnis fehlt oder enthält den Preview-Code nicht. Bestätigung bleibt gesperrt.</p>}
        {preview.warnings.map((warning) => <p key={warning} className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">Warnung: {warning}</p>)}
        {preview.inferredChildAsins.length > 0 && <p>Name-auto-Zuordnung für: <span className="font-mono">{preview.inferredChildAsins.join(', ')}</span></p>}
        {preview.blockers.length > 0 && <p role="alert" className="rounded border border-red-300 bg-red-50 p-2 text-red-800">Blocker: {preview.blockers.join(', ')}</p>}
        {isConform ? <p className="font-semibold text-emerald-700">Bereits konform</p> : <>
          <label className="flex items-start gap-2"><input type="checkbox" checked={familyConfirmed} onChange={(event) => setFamilyConfirmed(event.target.checked)} disabled={IS_DEMO_MODE || !directoryReady || preview.blockers.length > 0} />{EXCLUSIVE_FAMILY_CONFIRMATION}</label>
          {needsInferenceConfirmation && <label className="flex items-start gap-2"><input type="checkbox" checked={inferenceConfirmed} onChange={(event) => setInferenceConfirmed(event.target.checked)} disabled={IS_DEMO_MODE || !directoryReady || preview.blockers.length > 0} />Ich habe Familie und Code der name_auto-Zuordnung ausdrücklich geprüft.</label>}
          {IS_DEMO_MODE ? <p className="font-medium text-amber-800">Demo-Modus: Der Preview ist lesbar, Umbenennen und Zurücksetzen bleiben gesperrt.</p> : <button type="button" onClick={() => void handleRename()} disabled={!canRename} className="rounded bg-indigo-600 px-3 py-2 font-medium text-white disabled:opacity-50">{renameMutation.isPending ? 'Wird umbenannt…' : 'Umbenennen bestätigen'}</button>}
        </>}
      </div>}

      {(message || reconciliationRequired) && <p role="alert" className={`mt-3 rounded p-2 text-xs ${reconciliationRequired || messageKind === 'reconcile' ? 'bg-amber-50 text-amber-900' : messageKind === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>{reconciliationRequired ? 'Ausgang der letzten Änderung unklar. Ein Abgleich ist erforderlich; es wurde nicht automatisch wiederholt.' : message}</p>}
      {canRevert && <button type="button" onClick={() => void handleRevert()} disabled={revertMutation.isPending} className="mt-3 rounded border border-amber-600 px-3 py-2 text-xs font-medium text-amber-800 disabled:opacity-50">{revertMutation.isPending ? 'Wird zurückgesetzt…' : 'Auf Originalnamen zurücksetzen'}</button>}
      {aliases.length > 0 && <div className="mt-3 text-xs"><h3 className="font-semibold">Namenshistorie</h3><ul>{aliases.map((alias) => <li key={alias.id}><span className="font-mono">{alias.oldName}</span> → <span className="font-mono">{alias.newName}</span></li>)}</ul></div>}
    </Card>
  )
}
